import type {
  AftercareInputs,
  EmotionalNeed,
  Intensity,
  PhysicalNeed,
  SceneType,
  TimeAvailable,
} from '../data/types';
import styles from './Questionnaire.module.css';

interface QuestionnaireProps {
  inputs: AftercareInputs;
  onChange: (next: AftercareInputs) => void;
  onSubmit: () => void;
}

const SCENE_TYPES: { value: SceneType; label: string }[] = [
  { value: 'impact', label: 'impact' },
  { value: 'sensation', label: 'sensation' },
  { value: 'bondage', label: 'bondage' },
  { value: 'psychological', label: 'psychological' },
  { value: 'primal', label: 'primal' },
  { value: 'mixed', label: 'mixed' },
];

const INTENSITIES: Intensity[] = ['light', 'moderate', 'heavy', 'edge'];
const DURATIONS = [
  { value: 'under-hour', label: 'under an hour' },
  { value: '1-3-hours', label: '1–3 hours' },
  { value: 'longer', label: 'longer' },
] as const;
const WEIGHTS: { value: AftercareInputs['emotionalWeight']; label: string }[] = [
  { value: 'light', label: 'light' },
  { value: 'moderate', label: 'moderate' },
  { value: 'heavy', label: 'heavy' },
];
const DROPS: { value: AftercareInputs['dropPattern']; label: string }[] = [
  { value: 'rare', label: 'rare' },
  { value: 'occasional', label: 'occasional' },
  { value: 'common', label: 'common' },
  { value: 'severe', label: 'sometimes severe' },
];
const PHYSICAL: PhysicalNeed[] = ['warmth', 'hydration', 'food', 'sleep', 'movement'];
const EMOTIONAL: EmotionalNeed[] = ['silence', 'talking', 'touch', 'witnessing', 'solitude'];
const TIMES: { value: TimeAvailable; label: string }[] = [
  { value: '15min', label: '15 min' },
  { value: '30min', label: '30 min' },
  { value: '1hour', label: '1 hour' },
  { value: 'overnight', label: 'overnight' },
];

export function Questionnaire({ inputs, onChange, onSubmit }: QuestionnaireProps) {
  function setField<K extends keyof AftercareInputs>(key: K, value: AftercareInputs[K]) {
    onChange({ ...inputs, [key]: value });
  }
  function toggleArr<T extends PhysicalNeed | EmotionalNeed>(
    key: 'physicalNeeds' | 'emotionalNeeds',
    value: T
  ) {
    const arr = inputs[key] as T[];
    const next = arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
    onChange({ ...inputs, [key]: next });
  }

  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <fieldset className={styles.field}>
        <legend className={styles.legend}>scene type</legend>
        <p className={styles.subtitle}>what shape did the play take?</p>
        <div className={styles.chips}>
          {SCENE_TYPES.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              className={`${styles.chip} ${inputs.sceneType === value ? styles.chipActive : ''}`}
              onClick={() => setField('sceneType', value)}
            >
              {label}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className={styles.field}>
        <legend className={styles.legend}>intensity</legend>
        <p className={styles.subtitle}>not a value judgment — just an honest read.</p>
        <div className={styles.chips}>
          {INTENSITIES.map((value) => (
            <button
              key={value}
              type="button"
              className={`${styles.chip} ${inputs.intensity === value ? styles.chipActive : ''}`}
              onClick={() => setField('intensity', value)}
            >
              {value}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className={styles.field}>
        <legend className={styles.legend}>duration of the scene</legend>
        <div className={styles.chips}>
          {DURATIONS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              className={`${styles.chip} ${inputs.duration === value ? styles.chipActive : ''}`}
              onClick={() => setField('duration', value)}
            >
              {label}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className={styles.field}>
        <legend className={styles.legend}>emotional weight</legend>
        <p className={styles.subtitle}>how heavy did it land, inside you?</p>
        <div className={styles.chips}>
          {WEIGHTS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              className={`${styles.chip} ${
                inputs.emotionalWeight === value ? styles.chipActive : ''
              }`}
              onClick={() => setField('emotionalWeight', value)}
            >
              {label}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className={styles.field}>
        <legend className={styles.legend}>your drop pattern</legend>
        <p className={styles.subtitle}>how often does drop arrive in the days after?</p>
        <div className={styles.chips}>
          {DROPS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              className={`${styles.chip} ${inputs.dropPattern === value ? styles.chipActive : ''}`}
              onClick={() => setField('dropPattern', value)}
            >
              {label}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className={styles.field}>
        <legend className={styles.legend}>physical needs (choose any)</legend>
        <div className={styles.chips}>
          {PHYSICAL.map((value) => (
            <button
              key={value}
              type="button"
              className={`${styles.chip} ${
                inputs.physicalNeeds.includes(value) ? styles.chipActive : ''
              }`}
              onClick={() => toggleArr('physicalNeeds', value)}
            >
              {value}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className={styles.field}>
        <legend className={styles.legend}>emotional needs (choose any)</legend>
        <div className={styles.chips}>
          {EMOTIONAL.map((value) => (
            <button
              key={value}
              type="button"
              className={`${styles.chip} ${
                inputs.emotionalNeeds.includes(value) ? styles.chipActive : ''
              }`}
              onClick={() => toggleArr('emotionalNeeds', value)}
            >
              {value}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className={styles.field}>
        <legend className={styles.legend}>time available for aftercare</legend>
        <div className={styles.chips}>
          {TIMES.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              className={`${styles.chip} ${
                inputs.timeAvailable === value ? styles.chipActive : ''
              }`}
              onClick={() => setField('timeAvailable', value)}
            >
              {label}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className={styles.field}>
        <legend className={styles.legend}>name this recipe (optional)</legend>
        <input
          type="text"
          className={styles.input}
          placeholder="e.g. after a heavy bondage scene"
          value={inputs.recipeName ?? ''}
          maxLength={80}
          onChange={(e) => setField('recipeName', e.target.value)}
        />
      </fieldset>

      <button type="submit" className={styles.submit}>
        generate the recipe →
      </button>
    </form>
  );
}
